/**
 * Run the WebSocket bridge: either run the auth exchange (pending) or create the permanent bridge connection.
 */

import { writeConfigForApi } from './config.js';
import { log, logImmediate } from './log.js';
import { installBridgeProcessResilience } from './process-bridge-resilience.js';
import { runPendingAuth } from './auth/run-pending-auth.js';
import { runConnectedBridge } from './run-bridge-connected.js';
import { requestCliImmediateShutdown } from './runtime/cli-process-interrupt.js';
import type { RunBridgeOptions } from './run-bridge-options.js';
export type { RunBridgeOptions } from './run-bridge-options.js';

export async function runBridge(options: RunBridgeOptions): Promise<void> {
  installBridgeProcessResilience();
  const {
    apiUrl,
    workspaceId,
    authToken,
    bridgeName,
    worktreesRootPath,
    e2eCertificate,
  } = options;
  const firehoseServerUrl = options.firehoseServerUrl ?? options.proxyServerUrl;

  const hasAuth = workspaceId && authToken;

  if (!hasAuth) {
    const handle = runPendingAuth({
      apiUrl,
      initialWorkspaceId: workspaceId,
      preferredBridgeName: bridgeName,
      log,
      onAuth: (_auth) => {
        // Token received; authPromise will resolve and we'll run the bridge below
      },
    });
    const onSignal = (kind: 'interrupt' | 'stop') => {
      requestCliImmediateShutdown();
      logImmediate(
        kind === 'interrupt'
          ? 'Keyboard interrupt (Ctrl+C) — stopping…'
          : 'Stop requested — shutting down…'
      );
      setImmediate(() => {
        handle.close();
        process.exit(0);
      });
    };
    const onSigInt = () => onSignal('interrupt');
    const onSigTerm = () => onSignal('stop');
    process.on('SIGINT', onSigInt);
    process.on('SIGTERM', onSigTerm);
    const auth = await handle.authPromise;
    process.off('SIGINT', onSigInt);
    process.off('SIGTERM', onSigTerm);
    handle.close();
    if (!auth) return;
    writeConfigForApi(apiUrl, {
      workspaceId: auth.workspaceId,
      token: auth.token,
      refreshToken: auth.refreshToken,
    });
    await runBridge({
      apiUrl,
      workspaceId: auth.workspaceId,
      authToken: auth.token,
      refreshToken: auth.refreshToken,
      firehoseServerUrl,
      bridgeName,
      justAuthenticated: true,
      worktreesRootPath,
      e2eCertificate,
    });
    return;
  }

  await runConnectedBridge(options, runBridge);
}
